import React, { useState, useContext, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faTachometerAlt,
  faCreditCard,
  faChartBar,
  faCog,
  faMapMarkerAlt,
  faCalculator,
  faGasPump,
  faSignOutAlt,
  faQuestionCircle,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { useSelector } from 'react-redux';
import AdminAccountSelector from './AdminAccountSelector';

const MobileHeader = ({ theme }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [selectedCardGroup, setSelectedCardGroup] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useContext(AuthContext);
  const userRoles = currentUser?.Roles || [];
  const isAdmin = currentUser?.isAdmin;
  const isContractAdmin = currentUser?.isContractAdmin;
  const isPolicyAdmin = currentUser?.isPolicyAdmin;
  
  const policies = useSelector((state) => state.policies.activePolicies);
  const cardGroups = useSelector((state) => state.policies.cardGroups);
  const policiesStatus = useSelector((state) => state.policies.status);

  const apiUrl = process.env.REACT_APP_API_URL;

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setIsMenuOpen(false); // Close the menu after navigation
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsMenuOpen(false); // Close the menu after logout
  };

  useEffect(() => {
    // Skip this logic for affiliate users who don't need policy/card group selection
    if (currentUser && !(currentUser.isAffiliate === 'True' || currentUser.isAffiliate === true || currentUser.Roles === 'Affiliate')) {
      if (currentUser.cardGroupID) {
        setSelectedCardGroup(currentUser.cardGroupID);
        setSelectedPolicy(null);
      } else {
        setSelectedPolicy(currentUser.policyNumber || "");
        setSelectedCardGroup(null);
      }
    }
  }, [currentUser?.policyNumber, currentUser?.cardGroupID, currentUser?.isAffiliate]);

  const handleAccountSelection = async (selection) => {
    const { value, selectedPolicy, selectedCardGroup } = selection;

    const policyNumber = selectedPolicy ? selectedPolicy.PolicyNumber : null;
    const cardGroupID = selectedCardGroup ? selectedCardGroup.cardGroupID : null;

    if (!policyNumber && !cardGroupID) {
      console.error('No valid policy number or card group selected');
      return;
    }

    setSelectedPolicy(policyNumber);
    setSelectedCardGroup(cardGroupID);

    try {
        const response = await fetch(`${apiUrl}/dataServer`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                action: 'updateAdminPolicyAndCardGroup',
                payload: {
                    userId: currentUser.userId,
                    selectedPolicy: policyNumber,
                    selectedCardGroup: cardGroupID,
                },
            }),
        });

        if (response.ok) {
            window.location.reload();
        } else {
            console.error('Failed to update Policy Number');
        }
    } catch (error) {
        console.error('An error occurred:', error);
    }
  };

  return (
    <div className="xl:hidden">
      {/* Modern Mobile Header */}
      <div className="bg-white shadow-md border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center justify-between px-4 py-3 w-full">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src={theme.logo}
              alt="Company Logo"
              className="h-10 w-auto"
              style={{ imageRendering: 'crisp-edges' }}
            />
          </div>

          {/* Redesigned Hamburger Menu Button */}
          <button
            onClick={toggleMenu}
            className="relative w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 focus:outline-none group"
            style={{
              backgroundColor: isMenuOpen ? `${theme.primary}10` : 'transparent'
            }}
          >
            <div className="relative w-6 h-6 flex flex-col justify-center items-center">
              <div
                className={`w-6 h-0.5 rounded-full transition-all duration-300 ease-out ${
                  isMenuOpen ? 'rotate-45 translate-y-0' : '-translate-y-1.5'
                }`}
                style={{ 
                  backgroundColor: isMenuOpen ? theme.primary : '#374151',
                  transformOrigin: 'center'
                }}
              />
              <div
                className={`w-6 h-0.5 rounded-full transition-all duration-300 ease-out ${
                  isMenuOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'
                }`}
                style={{ backgroundColor: '#374151' }}
              />
              <div
                className={`w-6 h-0.5 rounded-full transition-all duration-300 ease-out ${
                  isMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-1.5'
                }`}
                style={{ 
                  backgroundColor: isMenuOpen ? theme.primary : '#374151',
                  transformOrigin: 'center'
                }}
              />
            </div>
            
            {/* Ripple effect on click */}
            <div className={`absolute inset-0 rounded-full transition-all duration-300 ${
              isMenuOpen ? 'bg-white/20 scale-150' : 'scale-0'
            }`} />
          </button>
        </div>
      </div>
      
      {/* Mobile Menu Overlay with Smooth Animations */}
      <div className={`fixed inset-0 z-40 transition-all duration-300 ease-out pointer-events-none ${
        isMenuOpen ? 'pointer-events-auto' : ''
      }`}>
        <div 
          className={`absolute inset-0 bg-black transition-opacity duration-300 ease-out ${
            isMenuOpen ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setIsMenuOpen(false)}
        />
        <div className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-50 transform transition-all duration-300 ease-out ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <img 
                    src={theme.logo} 
                    alt="Logo" 
                    className="h-8 w-auto" 
                    style={{ imageRendering: 'crisp-edges' }}
                  />
                  <span className="text-lg font-semibold text-gray-900">Menu</span>
                </div>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Menu Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="px-4 py-2">
          <ul className="space-y-1">
            {/* Admin Dropdown for Policies/Card Groups */}
            {(isAdmin || isContractAdmin || isPolicyAdmin) && !currentUser?.isAffiliate && (
              <li className="mb-4">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                  <AdminAccountSelector
                    policies={policies}
                    cardGroups={cardGroups}
                    selectedValue={selectedCardGroup ? cardGroups.find(group => group.cardGroupID === selectedCardGroup)?.AccountNumber : selectedPolicy}
                    onSelectionChange={handleAccountSelection}
                    currentUser={currentUser}
                    theme={theme}
                    isMobile={true}
                    disabled={!(policies.length > 0 || cardGroups.length > 0)}
                    isLoading={currentUser && policiesStatus === 'loading'}
                  />
                </div>
              </li>
            )}

            {/* Navigation Section */}
            {!userRoles.includes('Driver') && !currentUser?.isAffiliate && (
              <>
                <li className="mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 mb-2">Main Navigation</p>
                </li>
                <li>
                  <button 
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                      location.pathname === "/dashboard" 
                        ? "border-l-4" 
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    style={{
                      ...(location.pathname === "/dashboard" && {
                        backgroundColor: theme.sidebarHoverBg || `${theme.primary}20`,
                        color: theme.primary,
                        borderLeftColor: theme.primary
                      })
                    }}
                    onClick={() => handleNavigate("/dashboard")}
                  >
                    <FontAwesomeIcon icon={faTachometerAlt} className="w-4 h-4" />
                    <span className="font-medium">Dashboard</span>
                  </button>
                </li>
                <li>
                  <button 
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                      location.pathname === "/usercards" 
                        ? "border-l-4" 
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    style={{
                      ...(location.pathname === "/usercards" && {
                        backgroundColor: theme.sidebarHoverBg || `${theme.primary}20`,
                        color: theme.primary,
                        borderLeftColor: theme.primary
                      })
                    }}
                    onClick={() => handleNavigate("/usercards")}
                  >
                    <FontAwesomeIcon icon={faCreditCard} className="w-4 h-4" />
                    <span className="font-medium">Fuel Cards</span>
                  </button>
                </li>
                <li>
                  <button 
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                      location.pathname === "/userusers" 
                        ? "border-l-4" 
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    style={{
                      ...(location.pathname === "/userusers" && {
                        backgroundColor: theme.sidebarHoverBg || `${theme.primary}20`,
                        color: theme.primary,
                        borderLeftColor: theme.primary
                      })
                    }}
                    onClick={() => handleNavigate("/userusers")}
                  >
                    <FontAwesomeIcon icon={faUsers} className="w-4 h-4" />
                    <span className="font-medium">Users</span>
                  </button>
                </li>
                <li>
                  <button 
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                      location.pathname === "/usertransactions" 
                        ? "border-l-4" 
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    style={{
                      ...(location.pathname === "/usertransactions" && {
                        backgroundColor: theme.sidebarHoverBg || `${theme.primary}20`,
                        color: theme.primary,
                        borderLeftColor: theme.primary
                      })
                    }}
                    onClick={() => handleNavigate("/usertransactions")}
                  >
                    <FontAwesomeIcon icon={faGasPump} className="w-4 h-4" />
                    <span className="font-medium">Fuel Transactions</span>
                  </button>
                </li>
                <li>
                  <button 
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                      location.pathname === "/useraccounting" 
                        ? "border-l-4" 
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    style={{
                      ...(location.pathname === "/useraccounting" && {
                        backgroundColor: theme.sidebarHoverBg || `${theme.primary}20`,
                        color: theme.primary,
                        borderLeftColor: theme.primary
                      })
                    }}
                    onClick={() => handleNavigate("/useraccounting")}
                  >
                    <FontAwesomeIcon icon={faCalculator} className="w-4 h-4" />
                    <span className="font-medium">Accounting</span>
                  </button>
                </li>
                <li>
                  <button 
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                      location.pathname === "/userreporting" 
                        ? "border-l-4" 
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    style={{
                      ...(location.pathname === "/userreporting" && {
                        backgroundColor: theme.sidebarHoverBg || `${theme.primary}20`,
                        color: theme.primary,
                        borderLeftColor: theme.primary
                      })
                    }}
                    onClick={() => handleNavigate("/userreporting")}
                  >
                    <FontAwesomeIcon icon={faChartBar} className="w-4 h-4" />
                    <span className="font-medium">Reports</span>
                  </button>
                </li>
                <li className="my-4">
                  <div className="border-t border-gray-200"></div>
                </li>
              </>
            )}
            
            {/* General Section */}
            <li className="mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 mb-2">General</p>
            </li>
            <li>
              <button 
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                  location.pathname === "/locationfinder" 
                    ? "border-l-4" 
                    : "text-gray-700 hover:bg-gray-50"
                }`}
                style={{
                  ...(location.pathname === "/locationfinder" && {
                    backgroundColor: theme.sidebarHoverBg || `${theme.primary}20`,
                    color: theme.primary,
                    borderLeftColor: theme.primary
                  })
                }}
                onClick={() => handleNavigate("/locationfinder")}
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} className="w-4 h-4" />
                <span className="font-medium">Locations</span>
              </button>
            </li>
            <li>
              <button 
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                  location.pathname === "/faq" 
                    ? "border-l-4" 
                    : "text-gray-700 hover:bg-gray-50"
                }`}
                style={{
                  ...(location.pathname === "/faq" && {
                    backgroundColor: theme.sidebarHoverBg || `${theme.primary}20`,
                    color: theme.primary,
                    borderLeftColor: theme.primary
                  })
                }}
                onClick={() => handleNavigate("/faq")}
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="w-4 h-4" />
                <span className="font-medium">FAQ</span>
              </button>
            </li>
            <li>
              <button 
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                  location.pathname === "/settings" 
                    ? "border-l-4" 
                    : "text-gray-700 hover:bg-gray-50"
                }`}
                style={{
                  ...(location.pathname === "/settings" && {
                    backgroundColor: theme.sidebarHoverBg || `${theme.primary}20`,
                    color: theme.primary,
                    borderLeftColor: theme.primary
                  })
                }}
                onClick={() => handleNavigate("/settings")}
              >
                <FontAwesomeIcon icon={faCog} className="w-4 h-4" />
                <span className="font-medium">Settings</span>
              </button>
            </li>
            
            {/* Admin Section */}
            {((currentUser.hasSubaccounts && (currentUser.isAdmin || currentUser.isPolicyAdmin)) || (isAdmin || isContractAdmin)) && (
              <>
                <li className="my-4">
                  <div className="border-t border-gray-200"></div>
                </li>
                <li className="mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 mb-2">Administration</p>
                </li>
              </>
            )}
            
            {(currentUser.hasSubaccounts && (currentUser.isAdmin || currentUser.isPolicyAdmin)) && (
              <li>
                <button 
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                    location.pathname === "/accountmanager" 
                      ? "border-l-4" 
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                  style={{
                    ...(location.pathname === "/accountmanager" && {
                      backgroundColor: theme.sidebarHoverBg || `${theme.primary}20`,
                      color: theme.primary,
                      borderLeftColor: theme.primary
                    })
                  }}
                  onClick={() => handleNavigate('/accountmanager')}
                >
                  <FontAwesomeIcon icon={faCog} className="w-4 h-4" />
                  <span className="font-medium">Account Settings</span>
                </button>
              </li>
            )}
            
            {(isAdmin || isContractAdmin) && (
              <li>
                <button 
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
                    location.pathname === "/admin-settings" 
                      ? "border-l-4" 
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                  style={{
                    ...(location.pathname === "/admin-settings" && {
                      backgroundColor: theme.sidebarHoverBg || `${theme.primary}20`,
                      color: theme.primary,
                      borderLeftColor: theme.primary
                    })
                  }}
                  onClick={() => handleNavigate('/admin-settings')}
                >
                  <FontAwesomeIcon icon={faCog} className="w-4 h-4" />
                  <span className="font-medium">Admin Settings</span>
                </button>
              </li>
            )}
          </ul>
                </div>
              </div>

              {/* Footer with Logout */}
              <div className="border-t border-gray-100 p-4">
                <button 
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-all duration-200"
                  onClick={handleLogout}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="w-4 h-4" />
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default MobileHeader;




// import React, { useState, useContext, useEffect } from 'react';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import {
//   faBars,
//   faTachometerAlt,
//   faCreditCard,
//   faChartBar,
//   faCog,
//   faMapMarkerAlt,
//   faCalculator,
//   faGasPump,
//   faSignOutAlt,
//   faQuestionCircle,
//   faUsers,
// } from '@fortawesome/free-solid-svg-icons';
// import { useLocation, useNavigate } from 'react-router-dom';
// import { AuthContext } from './AuthContext';
// import { useSelector } from 'react-redux';


// const MobileHeader = ({ theme }) => {
//   const [isMenuOpen, setIsMenuOpen] = useState(false);
//   const location = useLocation();
//   const navigate = useNavigate();
//   const { currentUser, logout } = useContext(AuthContext);
//   const userRoles = currentUser?.Roles || [];
//   const [selectedPolicy, setSelectedPolicy] = useState(null);
//   const [selectedCardGroup, setSelectedCardGroup] = useState(null);

//   const {isAdmin, isContractAdmin, isPolicyAdmin} = currentUser;

//   const policies = useSelector((state) => state.policies.activePolicies);
//   const cardGroups = useSelector((state) => state.policies.cardGroups);

//   const toggleMenu = () => {
//     setIsMenuOpen(!isMenuOpen);
//   };

//   const handleNavigate = (path) => {
//     navigate(path);
//     setIsMenuOpen(false); // Close the menu after navigation
//   };

//   const handleLogout = async () => {
//     await logout();
//     navigate('/');
//     setIsMenuOpen(false); // Close the menu after logout
//   };

//   useEffect(() => {
//     if (currentUser) {
//       setSelectedPolicy(currentUser.policyNumber || "");
//       setSelectedCardGroup(currentUser.cardGroupID || "");
//     }
//   }, [currentUser]);

//   const handlePolicyChange = (e) => {
//     const selectedValue = e.target.value;
//     const selectedCardGroup = cardGroups.find(group => group.AccountNumber.toString() === selectedValue);
//     const selectedPolicy = selectedCardGroup 
//       ? policies.find(policy => policy.PolicyNumber === selectedCardGroup.PolicyNumber) 
//       : policies.find(policy => policy.PolicyNumber.toString() === selectedValue);
      
//     setSelectedPolicy(selectedPolicy?.PolicyNumber || "");
//     setSelectedCardGroup(selectedCardGroup?.cardGroupID || "");
//   };

//   return (
    // <div className='header-parent'>
    //   <div className="mobile-header md:hidden">
    //     {/* Invisible placeholder to balance the header */}
    //     <div className="header-placeholder"></div>

    //     {/* Centered logo and company name */}
    //     <div className="header-center">
    //       <img
    //         src={theme.logo}
    //         alt="Company Logo"
    //         className="header-logo"
    //       />
    //     </div>

    //     {/* Hamburger menu */}
    //     <FontAwesomeIcon
    //       icon={faBars}
    //       onClick={toggleMenu}
    //       className="text-lg cursor-pointer"
    //       style={{ marginRight: '15px' }}
    //     />
      
//       {/* Admin Dropdown for Policies/Card Groups */}
//       {(isAdmin || isContractAdmin || isPolicyAdmin) && (
//         <div className="w-full p-4 bg-gray-100">
//           <label className="mr-2 text-sm font-medium text-gray-700">Account:</label>
//           <select
//             value={selectedCardGroup || selectedPolicy}
//             onChange={handlePolicyChange}
//             className="w-full bg-gray-100 rounded-md p-2"
//           >
//             <option value="">Select a policy</option>
//             {policies.map(policy => (
//               <option key={policy.ID} value={policy.PolicyNumber}>
//                 {isPolicyAdmin ? policy.CompanyName || policy.Description : `${policy.PolicyNumber} - ${policy.CompanyName || policy.Description}`}
//               </option>
//             ))}
//             {cardGroups.map(group => (
//               <option key={group.cardGroupID} value={group.AccountNumber}>
//                 {group.groupName} ({group.AccountNumber})
//               </option>
//             ))}
//           </select>
//         </div>
        
//       )}
//       {isMenuOpen && (
//         <div className="mobile-menu">
//           <ul className="text-lg">
//           {!userRoles.includes('Driver') && (
//             <>
//             <li className={`menu-item ${location.pathname === "/dashboard" ? "selected" : ""}`}
//                 onClick={() => handleNavigate("/dashboard")}>
//               <FontAwesomeIcon icon={faTachometerAlt} className="mr-2" />
//               Dashboard
//             </li>
//             <li className={`menu-item ${location.pathname === "/usercards" ? "selected" : ""}`}
//                 onClick={() => handleNavigate("/usercards")}>
//               <FontAwesomeIcon icon={faCreditCard} className="mr-2" />
//               Fuel Cards
//             </li>
//             <li
//               className={`menu-item mb-2 ${location.pathname === "/userusers" ? "selected" : ""
//                 }`}
//               onClick={() => handleNavigate("/userusers")}
//               style={{ color: theme.menuText }}
//             >
//               <FontAwesomeIcon icon={faUsers} className="mr-2" />
//               Users
//             </li>
//             <li className={`menu-item ${location.pathname === "/usertransactions" ? "selected" : ""}`}
//                 onClick={() => handleNavigate("/usertransactions")}>
//               <FontAwesomeIcon icon={faGasPump} className="mr-2" />
//               Fuel Transactions
//             </li>
//             <li className={`menu-item ${location.pathname === "/useraccounting" ? "selected" : ""}`}
//                 onClick={() => handleNavigate("/useraccounting")}>
//               <FontAwesomeIcon icon={faCalculator} className="mr-2" />
//               Accounting
//             </li>
//             <li className={`menu-item ${location.pathname === "/userreporting" ? "selected" : ""}`}
//                 onClick={() => handleNavigate("/userreporting")}>
//               <FontAwesomeIcon icon={faChartBar} className="mr-2" />
//               Reports
//             </li>
//             </>
//             )}
//             <li className={`menu-item ${location.pathname === "/locationfinder" ? "selected" : ""}`}
//                 onClick={() => handleNavigate("/locationfinder")}>
//               <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
//               Locations
//             </li>            
//             <li className={`menu-item ${location.pathname === "/faq" ? "selected" : ""}`}
//                 onClick={() => handleNavigate("/faq")}>
//               <FontAwesomeIcon icon={faQuestionCircle} className="mr-2" />
//               FAQ
//             </li>
//             <li className={`menu-item ${location.pathname === "/settings" ? "selected" : ""}`} 
//                 onClick={() => handleNavigate("/settings")}>
//               <FontAwesomeIcon icon={faCog} className="mr-2" />
//               Settings
//             </li>
//             <li className="menu-item" onClick={handleLogout}>
//               <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
//               Logout
//             </li>
//             {(currentUser?.isAdmin || currentUser?.isContractAdmin) && (
//               <li className={`menu-item ${location.pathname === '/admin-settings' ? 'selected' : ''}`}
//                   onClick={() => handleNavigate('/admin-settings')}>
//                 <FontAwesomeIcon icon={faCog} className="mr-2" />
//                 Admin Settings
//               </li>
//             )}
//           </ul>
//         </div>
//       )}
//     </div>
//     </div>
//   );
// };

// export default MobileHeader;
